import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge, Table, Avatar } from '@/components/ui';
import { useConstructionPlans } from './hooks/usePlan';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { PlanStatus, PlanType } from './types/construction-plan.types';

const statusColors: Record<PlanStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  pending_approval: 'bg-amber-100 text-amber-600',
  approved: 'bg-blue-100 text-blue-600',
  active: 'bg-emerald-100 text-emerald-600',
  on_hold: 'bg-red-100 text-red-600',
  completed: 'bg-emerald-900 text-white',
  cancelled: 'bg-slate-400 text-white',
  archived: 'bg-slate-900 text-white',
};

const statusLabels: Record<PlanStatus, string> = {
  draft: 'Bản thảo',
  pending_approval: 'Chờ duyệt',
  approved: 'Đã duyệt',
  active: 'Đang triển khai',
  on_hold: 'Tạm dừng',
  completed: 'Hoàn thành',
  cancelled: 'Đã hủy',
  archived: 'Lưu trữ',
};

const typeLabels: Record<PlanType, string> = {
  master_plan: 'Kế hoạch tổng thể',
  weekly_plan: 'Kế hoạch tuần',
  daily_plan: 'Kế hoạch ngày',
  zone_plan: 'Kế hoạch theo Zone',
  phase_plan: 'Kế hoạch theo Giai đoạn',
  rework_plan: 'Kế hoạch sửa lỗi',
  handover_plan: 'Kế hoạch bàn giao',
};

export default function PlanListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    plans,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useConstructionPlans(companyId);

  return (
    <PageLayout
      title="Kế hoạch Thi công (Plans)"
      description="Trung tâm điều hành kế hoạch thi công trung hạn và dài hạn, kết nối BOQ với thực tế hiện trường."
      breadcrumbs={[{ label: 'Thi công' }, { label: 'Kế hoạch' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-history-line">Lịch sử phiên bản</Button>
          <AuthButton
            module="construction"
            action="create"
            variant="primary"
            icon="ri-add-line"
            onClick={() => navigate('./create')}
          >
            Lập kế hoạch mới
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Kế hoạch đang chạy"
            value={stats?.activePlans || 0}
            icon="ri-play-circle-line"
            variant="success"
          />
          <KPICard
            title="Chờ phê duyệt"
            value={stats?.pendingApproval || 0}
            icon="ri-time-line"
            variant="warning"
          />
          <KPICard
            title="Thời gian TB"
            value={Math.round(stats?.avgDurationDays || 0)}
            icon="ri-calendar-line"
            variant="primary"
            suffix=" ngày"
          />
          <KPICard
            title="Tổng kế hoạch"
            value={stats?.totalPlans || 0}
            icon="ri-folder-zip-line"
            variant="info"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mã kế hoạch, tên kế hoạch..."
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  },
                  {
                    id: 'type', label: 'Loại kế hoạch', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả loại' },
                      ...Object.entries(typeLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <Table
            columns={[
              { key: 'code', title: 'Mã hiệu' },
              { key: 'name', title: 'Tên kế hoạch & Phân loại' },
              { key: 'dates', title: 'Thời gian thực hiện' },
              { key: 'author', title: 'Người lập' },
              { key: 'status', title: 'Trạng thái', align: 'center' },
            ]}
            data={plans}
            isLoading={isLoading}
            onRowClick={(item) => navigate(`./detail/${item.id}`)}
            renderCell={(item, key) => {
              switch (key) {
                case 'code':
                  return <span className="font-mono text-[11px] font-bold text-os-primary  tracking-wider">{item.code}</span>;
                case 'name':
                  return (
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-900 line-clamp-1">{item.name}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-slate-400 font-bold ">{typeLabels[item.plan_type]}</span>
                        <span className="text-slate-300">•</span>
                        <span className="text-[10px] text-slate-400 font-medium">{item.project?.name}</span>
                      </div>
                    </div>
                  );
                case 'dates':
                  return (
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-slate-700">{new Date(item.start_date).toLocaleDateString('vi-VN')}</span>
                      <span className="text-[10px] text-slate-400 italic">đến {new Date(item.end_date).toLocaleDateString('vi-VN')}</span>
                    </div>
                  );
                case 'author':
                  return (
                    <div className="flex items-center gap-2">
                      <Avatar name={item.author?.full_name} size="xs" />
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold text-slate-700">{item.author?.full_name || '---'}</span>
                        <span className="text-[9px] text-slate-400 italic">Phiên bản v{item.version_no}</span>
                      </div>
                    </div>
                  );
                case 'status':
                  return (
                    <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                      {statusLabels[item.status_code]}
                    </Badge>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
